import { motion } from 'framer-motion';
import { Empty } from 'antd';
import { LucideIcon } from 'lucide-react';
import { isArray, isObject } from 'lodash';

interface ConfigurationCardProps {
	title: string;
	icon: LucideIcon;
	iconColor: string;
	iconBgColor: string;
	content: string | string[] | null | Record<string, any>;
	onCardClick: () => void;
	emptyDescription: string;
	isArrayContent?: boolean;
}

const cardVariants = {
	hidden: { opacity: 0, y: 20 },
	visible: { opacity: 1, y: 0 },
	exit: { opacity: 0, y: -20 },
};

const ConfigurationCard = ({
	title,
	icon: Icon,
	iconColor,
	iconBgColor,
	content,
	onCardClick,
	emptyDescription,
	isArrayContent = false,
}: ConfigurationCardProps) => {
	// Helper to truncate long text
	const truncateText = (text: string, maxLength = 120) => {
		if (!text) {
			return '';
		}
		return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
	};

	const renderContent = () => {
		if (!content || (isObject(content) && !isArray(content))) {
			return <Empty description={emptyDescription} image={Empty.PRESENTED_IMAGE_SIMPLE} />;
		}

		if (isArrayContent && Array.isArray(content)) {
			// Handle intake_questions_workflows structure
			if (
				content.length > 0 &&
				content[0] &&
				typeof content[0] === 'object' &&
				'customScenario' in content[0]
			) {
				return (
					<div className="flex flex-col gap-2">
						{content?.slice(0, 1)?.map((item: any, idx: number) => (
							<div key={idx} className="bg-gray-100 p-2 rounded-lg">
								<div className="font-semibold text-gray-800 mb-1">
									{truncateText(item.customScenario || 'Untitled Scenario', 60)}
								</div>
								{Array.isArray(item.questions) && item.questions.length > 0 && (
									<ul className="list-disc list-inside text-gray-800 text-sm mb-1">
										{item.questions
											.slice(0, 2)
											?.map((q: string, qIdx: number) => <li key={qIdx}>{truncateText(q, 40)}</li>)}
									</ul>
								)}
							</div>
						))}
					</div>
				);
			}
			// Handle regular string arrays
			return (
				<div className="points-list flex flex-col gap-2">
					{content.length > 0 ? (
						content.slice(0, 2).map((item: string, idx: number) => (
							<div key={idx} className="bg-gray-100 p-2 rounded-lg">
								<div
									className="text-gray-800 text-sm leading-5"
									style={{
										overflow: 'hidden',
										display: '-webkit-box',
										WebkitLineClamp: 2,
										WebkitBoxOrient: 'vertical',
										wordBreak: 'break-word',
										hyphens: 'auto',
									}}
								>
									{item}
								</div>
							</div>
						))
					) : (
						<Empty description={emptyDescription} image={Empty.PRESENTED_IMAGE_SIMPLE} />
					)}
				</div>
			);
		}

		return (
			<div className="bg-gray-100 p-2 rounded-lg">
				<div
					className="text-gray-800 text-sm leading-5"
					style={{
						overflow: 'hidden',
						display: '-webkit-box',
						WebkitLineClamp: 4,
						WebkitBoxOrient: 'vertical',
						wordBreak: 'break-word',
						hyphens: 'auto',
					}}
				>
					{content as string}
				</div>
			</div>
		);
	};

	return (
		<motion.div
			variants={cardVariants}
			initial="hidden"
			animate="visible"
			exit="exit"
			transition={{ duration: 0.3, ease: 'easeOut' }}
			className="border border-slate-200/80 rounded-[20px] p-6 transition-all bg-white shadow-sm relative overflow-hidden cursor-pointer"
			whileHover={{
				boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 10px rgba(0, 0, 0, 0.05)',
				y: -4,
			}}
			whileTap={{ scale: 0.98 }}
			onClick={onCardClick}
		>
			<div className="flex items-start gap-4 mb-4 relative">
				<div style={{ flex: 1, minWidth: 0 }}>
					<div className="flex items-center gap-3 mb-2">
						<div className={`${iconColor} flex-shrink-0 p-2 ${iconBgColor} rounded-xl shadow-sm`}>
							<Icon size={20} />
						</div>
						<h4 className="m-0 text-lg font-semibold text-gray-900 leading-snug tracking-tight">
							{title}
						</h4>
					</div>
					{renderContent()}
				</div>
			</div>
			<div
				className={`absolute bottom-4 right-5 ${iconColor} opacity-60 text-xs font-medium uppercase tracking-wider`}
			>
				Edit →
			</div>
		</motion.div>
	);
};

export default ConfigurationCard;
